import { apply, bump, celebrate, counter, onceToday } from '../core/effects';
import { logShadow, logSpell } from '../content/passport';
import { WORDS } from '../content/vocab';
import { S } from '../core/game';
import { m } from '../i18n';
import { sayEnglish } from '../audio/speech';
import { sfx } from '../audio/sfx';
import { h, type Child } from './dom';
import { refreshPanel } from './panel';
import { dictationScore, hasMic, listenFor, words } from './lifeMic';

/** "Listen" tab of the English corner: hear and pick (listening) or hear and type (dictation). */
type Mode = 'pick' | 'dict';
const st = {
  mode: 'pick' as Mode,
  text: '',
  options: [] as string[],
  picked: '' as string,
  typed: '',
  result: '' as '' | 'ok' | 'close' | 'bad',
  streak: 0,
  mic: '' as '' | 'listening' | 'ok' | 'bad',
};

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const shortEx = WORDS.filter((w) => words(w.ex).length <= 7);

/** Pick a new item; sentences get more likely as the grade goes up. */
export function nextListen(): void {
  const g = S().grade;
  const sentence = Math.random() < (g >= 5 ? 0.7 : g === 4 ? 0.45 : 0.25);
  st.picked = '';
  st.typed = '';
  st.result = '';
  st.mic = '';
  if (st.mode === 'pick') {
    if (sentence) {
      const w = shortEx[Math.floor(Math.random() * shortEx.length)];
      st.text = w.ex;
      const others = shuffle(shortEx.filter((x) => x !== w && x.cat === w.cat)).slice(0, 3);
      const fill = shuffle(shortEx.filter((x) => x !== w && !others.includes(x))).slice(0, 3 - others.length);
      st.options = shuffle([w.ex, ...[...others, ...fill].map((x) => x.ex)]);
    } else {
      const pool = WORDS.filter((w) => /^[a-z]+$/.test(w.w) && w.w.length <= (g <= 3 ? 6 : 12));
      const w = pool[Math.floor(Math.random() * pool.length)];
      st.text = w.w;
      // Similar-looking words make it a real listening task.
      const near = shuffle(pool.filter((x) => x !== w && (x.w[0] === w.w[0] || Math.abs(x.w.length - w.w.length) <= 1))).slice(0, 3);
      const fill = shuffle(pool.filter((x) => x !== w && !near.includes(x))).slice(0, 3 - near.length);
      st.options = shuffle([w.w, ...[...near, ...fill].map((x) => x.w)]);
    }
  } else {
    if (sentence && g >= 5) st.text = shortEx[Math.floor(Math.random() * shortEx.length)].ex;
    else {
      const pool = WORDS.filter((w) => /^[a-z]+$/.test(w.w) && w.w.length <= (g <= 3 ? 5 : g === 4 ? 7 : 10));
      st.text = pool[Math.floor(Math.random() * pool.length)].w;
    }
  }
  window.setTimeout(() => sayEnglish(st.text, 0.85), 250);
}

function reward(): void {
  st.streak++;
  if (onceToday(`listen.${st.text}`) && counter('lifeListenStars') < 15) {
    bump('lifeListenStars');
    celebrate();
    apply({ stars: 1, study: 1 });
  } else sfx('ok');
}

export function listenTab(): Child[] {
  const t = m().life.listen;
  if (!st.text) nextListen();
  const refresh = () => refreshPanel('english');
  const modes = h(
    'div',
    { class: 'chips life-listen-modes' },
    (['pick', 'dict'] as Mode[]).map((k) =>
      h('button', { class: `chip small${st.mode === k ? ' on' : ''}`, onclick: () => ((st.mode = k), sfx('click'), nextListen(), refresh()) }, t[k]),
    ),
  );
  const player = h(
    'div',
    { class: 'life-listen-player' },
    h('button', { class: 'life-big-play', 'aria-label': 'play', onclick: () => sayEnglish(st.text, 0.85) }, '🔊'),
    h('button', { class: 'btn small ghost', onclick: () => sayEnglish(st.text, 0.5) }, t.slow),
    st.streak > 1 ? h('span', { class: 'life-streak' }, t.streak(st.streak)) : null,
  );
  const answered = st.result !== '';
  const feedback = answered
    ? h(
        'div',
        { class: `life-listen-fb ${st.result}` },
        h('p', null, st.result === 'ok' ? `✅ ${t.ok}` : st.result === 'close' ? `🟡 ${t.close(st.text)}` : `❌ ${t.bad(st.text)}`),
        hasMic()
          ? h(
              'button',
              {
                class: `btn small blue${st.mic === 'listening' ? ' pulse' : ''}`,
                onclick: () => {
                  st.mic = 'listening';
                  refresh();
                  const started = listenFor(
                    st.text,
                    (good) => {
                      st.mic = good ? 'ok' : 'bad';
                      if (good) {
                        logShadow(st.text.toLowerCase().slice(0, 60));
                        sfx('ok');
                      } else sfx('bad');
                      refresh();
                    },
                    () => ((st.mic = ''), refresh()),
                  );
                  if (!started) st.mic = '';
                },
              },
              st.mic === 'listening' ? m().life.replay.listening : st.mic === 'ok' ? `✅ ${m().life.replay.good}` : st.mic === 'bad' ? `🔁 ${t.shadow}` : t.shadow,
            )
          : null,
        h('button', { class: 'btn small gold', onclick: () => (nextListen(), refresh()) }, t.next),
      )
    : null;

  if (st.mode === 'pick') {
    return [
      modes,
      h('p', { class: 'hint' }, t.pickHint),
      player,
      h(
        'div',
        { class: 'life-listen-opts' },
        st.options.map((o) =>
          h(
            'button',
            {
              class: `btn life-listen-opt${answered && o === st.text ? ' right' : ''}${answered && o === st.picked && o !== st.text ? ' wrong' : ''}`,
              disabled: answered,
              onclick: () => {
                st.picked = o;
                st.result = o === st.text ? 'ok' : 'bad';
                if (st.result === 'ok') {
                  logShadow(st.text.toLowerCase().slice(0, 60));
                  reward();
                } else {
                  st.streak = 0;
                  sfx('bad');
                }
                refresh();
              },
            },
            o,
          ),
        ),
      ),
      feedback,
    ];
  }

  const input = h('input', {
    class: 'life-dict-input',
    type: 'text',
    autocomplete: 'off',
    autocapitalize: 'off',
    spellcheck: 'false',
    placeholder: t.placeholder,
    value: st.typed,
    disabled: answered,
  }) as HTMLInputElement;
  const check = () => {
    st.typed = input.value;
    if (!st.typed.trim()) return;
    st.result = dictationScore(st.text, st.typed);
    if (st.result === 'ok') {
      logSpell(st.text.toLowerCase().slice(0, 60));
      reward();
    } else {
      st.streak = 0;
      sfx(st.result === 'close' ? 'pop' : 'bad');
    }
    refresh();
  };
  input.addEventListener('input', () => (st.typed = input.value));
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') check();
  });
  return [
    modes,
    h('p', { class: 'hint' }, t.dictHint),
    player,
    h('div', { class: 'life-dict' }, input, answered ? null : h('button', { class: 'btn gold', onclick: check }, t.check)),
    feedback,
  ];
}
